import * as THREE from 'three';
import { clamp, lerp, smoothstep } from '../core/math';
import { Random } from '../core/Random';
import { updateNightGlow } from './materials';
import { glowTexture } from './textures';

interface SkyKey {
  h: number;
  top: string;
  horizon: string;
  sun: number;
  hemi: number;
  light: string;
}

const KEYS: readonly SkyKey[] = [
  { h: 0, top: '#060a1c', horizon: '#141a38', sun: 0.32, hemi: 0.32, light: '#8fa6ff' },
  { h: 4.8, top: '#0a1130', horizon: '#1d2446', sun: 0.32, hemi: 0.34, light: '#8fa6ff' },
  { h: 6, top: '#3a4a7a', horizon: '#ec8c5c', sun: 0.6, hemi: 0.5, light: '#ffb07a' },
  { h: 7.5, top: '#4f86c6', horizon: '#f3d0a8', sun: 1.9, hemi: 0.8, light: '#ffe2c0' },
  { h: 12, top: '#3586d8', horizon: '#cfe6f4', sun: 2.9, hemi: 1.0, light: '#fffaf0' },
  { h: 17, top: '#4583c6', horizon: '#f2dab0', sun: 2.5, hemi: 0.9, light: '#fff0d8' },
  { h: 19.2, top: '#3d5591', horizon: '#ff9a5c', sun: 1.3, hemi: 0.6, light: '#ffb070' },
  { h: 20.5, top: '#1b2350', horizon: '#83506a', sun: 0.45, hemi: 0.38, light: '#c08aa0' },
  { h: 21.6, top: '#060a1c', horizon: '#141a38', sun: 0.32, hemi: 0.32, light: '#8fa6ff' },
  { h: 24, top: '#060a1c', horizon: '#141a38', sun: 0.32, hemi: 0.32, light: '#8fa6ff' },
];

const STORM_TOP = new THREE.Color('#2d3a32');
const STORM_HORIZON = new THREE.Color('#6f8a62');
const MURK_TOP = new THREE.Color('#5a5f52');
const MURK_HORIZON = new THREE.Color('#77745a');
const MURK_LIGHT = new THREE.Color('#d8d0a0');
const UP = new THREE.Vector3(0, 1, 0);
const INDOOR_DARK = new THREE.Color('#0e0b09');
const INDOOR_LIGHT = new THREE.Color('#ffe2bd');
const INDOOR_HEMI = new THREE.Color('#fff1dc');
/** Light bouncing up off the floor and furniture. */
const INDOOR_GROUND = new THREE.Color('#a8825a');
const OUTDOOR_GROUND = new THREE.Color('#5a6b3a');
/** Indoors the "sun" becomes the ceiling light: nearly overhead, warm. */
const INDOOR_DIR = new THREE.Vector3(0.28, 1, 0.42).normalize();

/** Day/night cycle: gradient dome, sun/moon light with a player-following shadow camera, stars and clouds. */
export class Sky {
  readonly sun: THREE.DirectionalLight;
  readonly hemi: THREE.HemisphereLight;
  readonly dome: THREE.Mesh;
  /** 0 = full day, 1 = full night. */
  darkness = 0;
  /** 0..1: how deep into the Back Forty you are. Gloom, close fog and a sickly tint. */
  murk = 0;
  /** 0..1, driven by weather events (tornado). */
  storm = 0;
  /** 1 while you're inside a room: dark surroundings and a warm overhead light. */
  indoors = 0;

  private readonly uniforms = {
    top: { value: new THREE.Color() },
    horizon: { value: new THREE.Color() },
    sunDir: { value: new THREE.Vector3(0, 1, 0) },
    sunGlow: { value: 0 },
  };
  private readonly stars: THREE.Points;
  private readonly moon: THREE.Sprite;
  private readonly clouds = new THREE.Group();
  private readonly fog: THREE.Fog;
  private readonly tmpTop = new THREE.Color();
  private readonly tmpHor = new THREE.Color();
  private readonly tmpLight = new THREE.Color();
  private readonly sunDir = new THREE.Vector3();
  private readonly snapRight = new THREE.Vector3();
  private readonly snapUp = new THREE.Vector3();
  private readonly snapped = new THREE.Vector3();

  constructor(scene: THREE.Scene, shadowSize: number) {
    this.fog = new THREE.Fog('#cfe6f4', 70, 330);
    scene.fog = this.fog;

    this.hemi = new THREE.HemisphereLight('#cfe6ff', '#5a6b3a', 0.9);
    scene.add(this.hemi);

    this.sun = new THREE.DirectionalLight('#fffaf0', 2.5);
    this.sun.castShadow = shadowSize > 0;
    if (shadowSize > 0) {
      this.sun.shadow.mapSize.set(shadowSize, shadowSize);
      const cam = this.sun.shadow.camera;
      cam.left = -45;
      cam.right = 45;
      cam.top = 45;
      cam.bottom = -45;
      cam.near = 1;
      cam.far = 220;
      this.sun.shadow.bias = -0.0006;
      this.sun.shadow.normalBias = 0.04;
      // Soften the edges a few texels so the shadow map's stair-steps don't show.
      this.sun.shadow.radius = 3;
    }
    scene.add(this.sun, this.sun.target);

    const domeMat = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 top;
        uniform vec3 horizon;
        uniform vec3 sunDir;
        uniform float sunGlow;
        varying vec3 vDir;
        void main() {
          float h = vDir.y;
          vec3 col = h > 0.0 ? mix(horizon, top, pow(h, 0.55)) : mix(horizon, horizon * 0.55, min(1.0, -h * 4.0));
          float s = max(dot(vDir, sunDir), 0.0);
          col += vec3(1.0, 0.85, 0.6) * (pow(s, 400.0) * 2.0 + pow(s, 12.0) * 0.25) * sunGlow;
          gl_FragColor = vec4(col, 1.0);
        }`,
    });
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(450, 32, 16), domeMat);
    this.dome.renderOrder = -10;
    this.dome.frustumCulled = false;
    scene.add(this.dome);

    const rng = new Random(7);
    const starPos: number[] = [];
    for (let i = 0; i < 900; i++) {
      const theta = rng.range(0, Math.PI * 2);
      const y = rng.range(0.08, 1);
      const r = Math.sqrt(1 - y * y);
      starPos.push(Math.cos(theta) * r * 430, y * 430, Math.sin(theta) * r * 430);
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.Float32BufferAttribute(starPos, 3));
    this.stars = new THREE.Points(
      starGeo,
      new THREE.PointsMaterial({
        color: '#ffffff',
        size: 1.6,
        sizeAttenuation: false,
        transparent: true,
        opacity: 0,
        fog: false,
        depthWrite: false,
      }),
    );
    this.stars.frustumCulled = false;
    this.dome.add(this.stars);

    this.moon = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTexture(),
        color: '#f4f1e0',
        transparent: true,
        fog: false,
        depthWrite: false,
      }),
    );
    this.moon.scale.setScalar(36);
    this.moon.position.set(-160, 260, -260);
    this.dome.add(this.moon);

    const cloudMat = new THREE.MeshStandardMaterial({
      color: '#ffffff',
      roughness: 1,
      flatShading: true,
      fog: false,
    });
    for (let i = 0; i < 14; i++) {
      const cloud = new THREE.Group();
      const n = rng.int(3, 6);
      for (let j = 0; j < n; j++) {
        const puff = new THREE.Mesh(new THREE.IcosahedronGeometry(rng.range(6, 11), 0), cloudMat);
        puff.position.set(j * 8 - n * 4, rng.range(-2, 2), rng.range(-4, 4));
        puff.scale.y = 0.55;
        cloud.add(puff);
      }
      cloud.position.set(rng.range(-300, 300), rng.range(85, 120), rng.range(-300, 300));
      this.clouds.add(cloud);
    }
    scene.add(this.clouds);
  }

  update(minutes: number, dt: number, focus: THREE.Vector3, camera: THREE.Camera): void {
    const h = (minutes / 60) % 24;
    let i = 0;
    while (i < KEYS.length - 2 && h >= (KEYS[i + 1] as SkyKey).h) i++;
    const a = KEYS[i] as SkyKey;
    const b = KEYS[i + 1] as SkyKey;
    const t = clamp((h - a.h) / (b.h - a.h), 0, 1);

    this.tmpTop.set(a.top).lerp(new THREE.Color(b.top), t);
    this.tmpHor.set(a.horizon).lerp(new THREE.Color(b.horizon), t);
    this.tmpLight.set(a.light).lerp(new THREE.Color(b.light), t);
    let sunI = lerp(a.sun, b.sun, t);
    let hemiI = lerp(a.hemi, b.hemi, t);

    if (this.storm > 0) {
      this.tmpTop.lerp(STORM_TOP, this.storm * 0.85);
      this.tmpHor.lerp(STORM_HORIZON, this.storm * 0.8);
      sunI *= 1 - this.storm * 0.65;
      hemiI *= 1 - this.storm * 0.35;
    }
    if (this.murk > 0) {
      const m = this.murk;
      this.tmpTop.lerp(MURK_TOP, m * 0.55);
      this.tmpHor.lerp(MURK_HORIZON, m * 0.7);
      this.tmpLight.lerp(MURK_LIGHT, m * 0.6);
      sunI *= 1 - m * 0.45;
      hemiI *= 1 - m * 0.12;
    }

    // How dark it is outside, whether or not you're out there.
    this.darkness = clamp(1 - (sunI - 0.32) / 1.4, 0, 1);
    if (this.storm > 0) this.darkness = Math.max(this.darkness, this.storm * 0.6);

    const k = this.indoors;
    let fogNear = lerp(lerp(70, 25, this.storm), 12, this.murk);
    let fogFar = lerp(lerp(330, 150, this.storm), 105, this.murk);
    if (k > 0) {
      this.tmpTop.lerp(INDOOR_DARK, k);
      this.tmpHor.lerp(INDOOR_DARK, k);
      this.tmpLight.lerp(INDOOR_LIGHT, k);
      sunI = lerp(sunI, 1.15 + 0.25 * (1 - this.darkness), k);
      // Eye-level views (first person, the bar games) need the walls lit, not just the floor.
      hemiI = lerp(hemiI, 1.25, k);
      fogNear = lerp(fogNear, 16, k);
      fogFar = lerp(fogFar, 42, k);
    }

    this.uniforms.top.value.copy(this.tmpTop);
    this.uniforms.horizon.value.copy(this.tmpHor);
    this.fog.color.copy(this.tmpHor);
    this.fog.near = fogNear;
    this.fog.far = fogFar;

    // Sun arc: rises in the east (+x), sets in the west, leaning south (+z).
    const dayT = (h - 6) / 14;
    const elev = Math.sin(clamp(dayT, 0, 1) * Math.PI);
    const daylight = dayT > 0 && dayT < 1;
    if (daylight) {
      const az = lerp(0.1, Math.PI - 0.1, dayT);
      this.sunDir.set(Math.cos(az), Math.max(0.12, elev), 0.35).normalize();
    } else {
      this.sunDir.set(-0.45, 0.85, -0.35).normalize(); // moonlight
    }
    if (k > 0) this.sunDir.lerp(INDOOR_DIR, k).normalize();
    this.uniforms.sunDir.value.copy(this.sunDir);
    this.uniforms.sunGlow.value = daylight ? smoothstep(0, 0.15, elev) * (1 - this.storm) * (1 - k) : 0;

    this.sun.color.copy(this.tmpLight);
    this.sun.intensity = sunI;
    // Snap the shadow camera to whole shadow-map texels, or shadow edges shimmer and crawl
    // every time you take a step.
    const cam = this.sun.shadow.camera;
    const texel = (cam.right - cam.left) / this.sun.shadow.mapSize.x;
    this.snapRight.crossVectors(UP, this.sunDir).normalize();
    this.snapUp.crossVectors(this.sunDir, this.snapRight);
    const fx = focus.dot(this.snapRight);
    const fy = focus.dot(this.snapUp);
    this.snapped
      .copy(focus)
      .addScaledVector(this.snapRight, Math.round(fx / texel) * texel - fx)
      .addScaledVector(this.snapUp, Math.round(fy / texel) * texel - fy);
    this.sun.position.copy(this.snapped).addScaledVector(this.sunDir, 110);
    this.sun.target.position.copy(this.snapped);
    this.hemi.intensity = hemiI;
    this.hemi.color.copy(this.tmpTop).lerp(new THREE.Color('#ffffff'), 0.55);
    this.hemi.groundColor.copy(OUTDOOR_GROUND);
    if (k > 0) {
      this.hemi.color.lerp(INDOOR_HEMI, k);
      this.hemi.groundColor.lerp(INDOOR_GROUND, k);
    }

    updateNightGlow(this.darkness);

    const starAlpha = clamp((this.darkness - 0.55) * 2.5, 0, 1) * (1 - this.storm) * (1 - k);
    (this.stars.material as THREE.PointsMaterial).opacity = starAlpha;
    (this.moon.material as THREE.SpriteMaterial).opacity = starAlpha;
    this.dome.position.copy(camera.position);

    const cloudTint = lerp(1, 0.22, this.darkness);
    const cm = (this.clouds.children[0]?.children[0] as THREE.Mesh | undefined)?.material as
      THREE.MeshStandardMaterial | undefined;
    if (cm) cm.color.setScalar(cloudTint * (1 - this.storm * 0.5));
    this.clouds.visible = k < 0.5;
    for (const c of this.clouds.children) {
      c.position.x += dt * (2 + this.storm * 25);
      if (c.position.x > 320) c.position.x = -320;
    }
  }
}
